import { type ReactNode } from 'react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { observer } from 'mobx-react-lite'
import { ActionCard } from '@/features/jobs/details/components/ActionCard'
import {
  Card,
  CardContent,
  CardContentDecoration,
  CardSubtitle,
  CardContentFooterActions,
  CardTitle,
} from '@/components/Card'
import { Icon } from '@/components/Icon'
import { StyledLink } from '@/components/Link'
import { ScrolledAction } from '@/features/jobs/details/components/PageHeader'
import { AutoSkeleton, IsSkeletonContext } from '@/components/Skeleton'
import { dateFormatters } from '@/utils/date-util'

export interface ReviewDraftDialogProps {
  children?: ReactNode
  isOpen: boolean
  onClose: () => unknown
  onConfirmApprove: () => unknown
  onConfirmReject: () => unknown
  testId?: string
  approveDraftButtonTestId?: string
  rejectDraftButtonTestId?: string
  notNowButtonTestId?: string
}

/**
 * Dialog for reviewing a draft.
 */
export function ReviewDraftDialog({
  children,
  isOpen,
  onClose,
  onConfirmApprove,
  onConfirmReject,
  testId,
  approveDraftButtonTestId,
  rejectDraftButtonTestId,
  notNowButtonTestId,
}: ReviewDraftDialogProps) {
  return (
    <ModalDialog isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>Review your latest draft</ModalDialogTitle>
        <ModalDialogDescription>
          Once accepted, the Pro will be able to close the job.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Approve draft"
            variant="default"
            onPress={onConfirmApprove}
            testId={approveDraftButtonTestId}
          />
          <Button
            label="Reject draft"
            variant="outline"
            onPress={onConfirmReject}
            testId={rejectDraftButtonTestId}
          />
          <Button
            label="Not now"
            variant="text"
            onPress={onClose}
            testId={notNowButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface ConfirmDraftApprovalDialogProps {
  isOpen: boolean
  isPending: boolean
  onClose: () => unknown
  onConfirm: () => unknown
  testId?: string
  confirmButtonTestId?: string
  goBackButtonTestId?: string
}

export function ConfirmDraftApprovalDialog({
  isOpen,
  isPending,
  onClose,
  onConfirm,
  testId,
  confirmButtonTestId,
  goBackButtonTestId,
}: ConfirmDraftApprovalDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          Are you sure that you approve of this draft?
        </ModalDialogTitle>
        <ModalDialogDescription>
          Once accepted, the Pro will be able to finalize and submit your
          return.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Yes, I approve"
            variant="default"
            onPress={onConfirm}
            isPending={isPending}
            testId={confirmButtonTestId}
          />
          <Button
            label="Go back"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
            testId={goBackButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface ConfirmDraftRejectionDialogProps {
  isOpen: boolean
  isPending: boolean
  onClose: () => unknown
  onConfirm: () => unknown
  testId?: string
  confirmButtonTestId?: string
  goBackButtonTestId?: string
}

export function ConfirmDraftRejectionDialog({
  isOpen,
  isPending,
  onClose,
  onConfirm,
  testId,
  confirmButtonTestId,
  goBackButtonTestId,
}: ConfirmDraftRejectionDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          Are you sure that you want to reject this draft?
        </ModalDialogTitle>
        <ModalDialogDescription>
          Please be sure to communicate changes you need to your Pro.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Reject & request changes"
            variant="default"
            onPress={onConfirm}
            isPending={isPending}
            testId={confirmButtonTestId}
          />
          <Button
            label="Go back"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
            testId={goBackButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface ReviewDraftDialogContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Review Draft Dialog Container
 */
export const ReviewDraftDialogContainer = observer(
  function ReviewDraftDialogContainer({
    vm: { progressionVm },
  }: ReviewDraftDialogContainerProps) {
    const isApproving = progressionVm.draftReviewVm.approveDraft.pending
    const isRejecting = progressionVm.draftReviewVm.rejectDraft.pending

    const draftReturnVm = progressionVm.draftReviewVm.draftReturnVm
    return (
      <>
        <ReviewDraftDialog
          isOpen={progressionVm.draftReviewVm.modal.showing}
          onClose={progressionVm.draftReviewVm.modal.close}
          onConfirmApprove={
            progressionVm.draftReviewVm.confirmDraftApprovalModalVm.show
          }
          onConfirmReject={
            progressionVm.draftReviewVm.confirmDraftRejectionModalVm.show
          }
          testId="review-draft-dialog"
          approveDraftButtonTestId="review-draft-dialog-approve-button"
          rejectDraftButtonTestId="review-draft-dialog-reject-button"
          notNowButtonTestId="review-draft-dialog-not-now-button"
        >
          {progressionVm.draftReviewVm.fetchDraftReturn.pending ||
          draftReturnVm ? (
            <Card>
              <CardContent>
                <IsSkeletonContext
                  value={progressionVm.draftReviewVm.fetchDraftReturn.pending}
                >
                  <CardContentDecoration>
                    <Icon name="file" variant="surface-background" />
                  </CardContentDecoration>

                  <CardTitle>
                    <AutoSkeleton>Latest draft return</AutoSkeleton>
                  </CardTitle>
                  <CardSubtitle>
                    <AutoSkeleton>
                      Uploaded by {draftReturnVm?.author?.displayName} on{' '}
                      {dateFormatters.dateTimeDefault.format(
                        draftReturnVm?.doc.dateCreated,
                      )}
                    </AutoSkeleton>
                  </CardSubtitle>
                  <CardContentFooterActions>
                    <StyledLink onPress={() => draftReturnVm?.preview()}>
                      <AutoSkeleton>View</AutoSkeleton>
                    </StyledLink>
                    <StyledLink onPress={() => draftReturnVm?.download()}>
                      <AutoSkeleton>Download</AutoSkeleton>
                    </StyledLink>
                  </CardContentFooterActions>
                </IsSkeletonContext>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <CardContentDecoration>
                  <Icon name="alert" variant="surface-background" />
                </CardContentDecoration>
                <CardTitle>No draft return document found</CardTitle>
                <CardSubtitle>
                  It doesn't look like you have a draft return for this job to
                  review yet. Please contact your Pro if you think this is an
                  error.
                </CardSubtitle>
              </CardContent>
            </Card>
          )}
        </ReviewDraftDialog>

        <ConfirmDraftApprovalDialog
          isOpen={
            progressionVm.draftReviewVm.confirmDraftApprovalModalVm.showing
          }
          onClose={
            progressionVm.draftReviewVm.confirmDraftApprovalModalVm.close
          }
          onConfirm={progressionVm.draftReviewVm.approveDraft}
          isPending={isApproving}
          testId="confirm-draft-approval-dialog"
          confirmButtonTestId="confirm-draft-approval-dialog-confirm-button"
          goBackButtonTestId="confirm-draft-approval-dialog-go-back-button"
        />

        <ConfirmDraftRejectionDialog
          isOpen={
            progressionVm.draftReviewVm.confirmDraftRejectionModalVm.showing
          }
          onClose={
            progressionVm.draftReviewVm.confirmDraftRejectionModalVm.close
          }
          onConfirm={progressionVm.draftReviewVm.rejectDraft}
          isPending={isRejecting}
          testId="confirm-draft-rejection-dialog"
          confirmButtonTestId="confirm-draft-rejection-dialog-confirm-button"
          goBackButtonTestId="confirm-draft-rejection-dialog-go-back-button"
        />
      </>
    )
  },
)

interface ReviewDraftActionCardContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Container for the action card + dialog for reviewing a draft.
 */
export const ReviewDraftActionCardContainer = observer(
  function ReviewDraftDialogContainer({
    vm,
  }: ReviewDraftActionCardContainerProps) {
    return (
      <>
        <ActionCard kicker="Your draft is ready" title="Pending your review">
          <Button
            onPress={vm.progressionVm.draftReviewVm.reviewDraft}
            label="Review your draft"
            testId="review-draft-button"
          />
        </ActionCard>
      </>
    )
  },
)

interface ReviewDraftScrolledActionContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Scrolled action for the header.
 */
export const ReviewDraftScrolledActionContainer = observer(
  function ReviewDraftScrolledActionContainer({
    vm,
  }: ReviewDraftScrolledActionContainerProps) {
    return (
      <ScrolledAction
        label="Review your draft"
        onPress={vm.progressionVm.draftReviewVm.reviewDraft}
      />
    )
  },
)
